import {
  CreateUserData,
  CreateUserProfileResponse,
  GetCertificateReminderResponse,
  GetCompanyReferralDataResponse,
  GetCompanyReferralHistoryResponse,
  GetMeResponse,
  GetNotificationsResponse,
  GetPaginatedUsersResponse,
  GetUserResponse,
  UpdateCompanySettingsResponse,
  updateFirstAccessRequest,
  UpdateUserAvatarResponse,
  UpdateUserData,
  UpdateUserProfileResponse
} from '@/types/user';
import fetchData, { Methods } from '@/utils/api';
import {
  APIENDPOINT_AVATAR,
  APIENDPOINT_ARCHIVE,
  APIENDPOINT_RESTORE,
  APIENDPOINT_CERTIFICATE,
  APIENDPOINT_COMPANIES,
  APIENDPOINT_ME,
  APIENDPOINT_NOTIFICATIONS,
  APIENDPOINT_PAGINATED,
  APIENDPOINT_READ,
  APIENDPOINT_UNREAD,
  APIENDPOINT_INBOX,
  APIENDPOINT_REFERRAL_DATA,
  APIENDPOINT_REPRESENTATIVE,
  APIENDPOINT_SETTINGS,
  APIENDPOINT_USERS,
  APIENDPOINT_CERTIFICATE_EXPIRATION_REMINDER,
  APIENDPOINT_REGISTER,
  APIENDPOINT_FIRST_ACCESS,
  APIENDPOINT_ANNUAL_REPORT_FIRST_ACCESS,
  APIENDPOINT_COMPANY_REGISTRATION,
  APIENDPOINT_MEMBER
} from '@/constants/api_endpoints';
import { COMPANY_SETTINGS_KEYS } from '@/constants/user';
import { ApiResponse } from '@/types/api';
import { SendEresidencyReminderResponse, updateCertificateRequest } from '@/types/certificate';

export async function getMe(): Promise<GetMeResponse> {
  return fetchData(Methods.GET, APIENDPOINT_ME);
}

export async function getUser(id: string): Promise<GetUserResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}/${id}`);
}

export async function getNotifications(): Promise<GetNotificationsResponse> {
  return fetchData(Methods.GET, APIENDPOINT_NOTIFICATIONS);
}

export async function getInboxNotifications(
  unreadOnly: boolean,
  archived: boolean = false
): Promise<GetNotificationsResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_NOTIFICATIONS}${APIENDPOINT_INBOX}`, {
    unread_only: unreadOnly,
    archived
  });
}

export async function getPaginatedUsers(
  page: number,
  limit: number,
  search?: string,
  filters?: Object
): Promise<GetPaginatedUsersResponse> {
  return fetchData(
    Methods.GET,
    `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}${APIENDPOINT_PAGINATED}`,
    {
      page,
      limit,
      filters: JSON.stringify({
        ...filters,
        search
      })
    }
  );
}

export async function createUser(user: CreateUserData): Promise<CreateUserProfileResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}`, user);
}

export async function updateUserProfile(
  id: string,
  user: UpdateUserData
): Promise<UpdateUserProfileResponse> {
  return fetchData(Methods.PUT, `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}/${id}`, user);
}

export async function updateUserAvatar(
  id: string,
  avatar: string
): Promise<UpdateUserAvatarResponse> {
  return fetchData(
    Methods.PUT,
    `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}/${id}${APIENDPOINT_AVATAR}`,
    { data: avatar }
  );
}

export function updateCompanySettings(
  key: (typeof COMPANY_SETTINGS_KEYS)[number],
  value: string
): Promise<UpdateCompanySettingsResponse> {
  return fetchData(Methods.PUT, `${APIENDPOINT_COMPANIES}${APIENDPOINT_SETTINGS}/${key}`, {
    value
  });
}

export function getReferralData(): Promise<GetCompanyReferralDataResponse> {
  return fetchData(Methods.GET, `${APIENDPOINT_COMPANIES}${APIENDPOINT_REFERRAL_DATA}`);
}

export function getReferralHistory(
  page: number,
  limit: number,
  filters?: Object
): Promise<GetCompanyReferralHistoryResponse> {
  return fetchData(
    Methods.GET,
    `${APIENDPOINT_COMPANIES}${APIENDPOINT_REFERRAL_DATA}${APIENDPOINT_PAGINATED}`,
    {
      page,
      limit,
      filters: JSON.stringify(filters)
    }
  );
}

export function setUserAsRepresentative(id: string): Promise<ApiResponse> {
  return fetchData(Methods.PUT, `${APIENDPOINT_COMPANIES}${APIENDPOINT_REPRESENTATIVE}/${id}`);
}

export function deleteUser(id: string): Promise<ApiResponse> {
  return fetchData(Methods.DELETE, `${APIENDPOINT_COMPANIES}${APIENDPOINT_USERS}/${id}`);
}

export function markNotificationAsRead(id: string): Promise<ApiResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_NOTIFICATIONS}/${id}${APIENDPOINT_READ}`);
}

export function markAllNotificationsAsRead(): Promise<ApiResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_NOTIFICATIONS}${APIENDPOINT_READ}`);
}

export function markNotificationAsUnread(id: string): Promise<ApiResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_NOTIFICATIONS}/${id}${APIENDPOINT_UNREAD}`);
}

export function archiveNotification(id: string): Promise<ApiResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_NOTIFICATIONS}/${id}${APIENDPOINT_ARCHIVE}`);
}

export function restoreNotification(id: string, read: boolean): Promise<ApiResponse> {
  return fetchData(Methods.POST, `${APIENDPOINT_NOTIFICATIONS}/${id}${APIENDPOINT_RESTORE}`, {
    read
  });
}

export function getEresidencyReminder(): Promise<GetCertificateReminderResponse> {
  return fetchData(
    Methods.GET,
    `${APIENDPOINT_ME}${APIENDPOINT_CERTIFICATE}${APIENDPOINT_CERTIFICATE_EXPIRATION_REMINDER}`
  );
}

export function sendEresidencyReminder(): Promise<SendEresidencyReminderResponse> {
  return fetchData(
    Methods.POST,
    `${APIENDPOINT_COMPANY_REGISTRATION}${APIENDPOINT_CERTIFICATE}${APIENDPOINT_CERTIFICATE_EXPIRATION_REMINDER}`
  );
}

export function updateCertificate(data: updateCertificateRequest): Promise<ApiResponse> {
  return fetchData(
    Methods.PUT,
    `${APIENDPOINT_COMPANY_REGISTRATION}${APIENDPOINT_MEMBER}/${data.member_id}${APIENDPOINT_CERTIFICATE}`,
    data
  );
}

export function updateFirstAccess(data: updateFirstAccessRequest): Promise<ApiResponse> {
  return fetchData(
    Methods.PUT,
    `${APIENDPOINT_ME}${APIENDPOINT_REGISTER}${APIENDPOINT_FIRST_ACCESS}`,
    data
  );
}

export function updateAnnualReportFirstAccess(): Promise<ApiResponse> {
  return fetchData(Methods.PUT, `${APIENDPOINT_ME}${APIENDPOINT_ANNUAL_REPORT_FIRST_ACCESS}`);
}
